import React, { useEffect, useState, useCallback } from "react";
import { ArrowUpRight, X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { projects as projectsData } from "../../data/projects";
import { useReveal } from "../../hooks/useReveal";

const ProjectCard = ({ project, meta, index, span, media = "aspect-[4/5]", onOpen, t }) => {
  const { ref, inView } = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`project-card-${project.id}`}
      className={`group relative overflow-hidden hover-zoom fade-in-up ${inView ? "in-view" : ""} ${span} cursor-pointer h-full`}
      style={{ transitionDelay: `${index * 80}ms` }}
      onClick={() => onOpen(project.id)}
    >
      <div className={`${media} w-full`}>
        <img
          src={project.cover}
          alt={meta.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8 text-white pointer-events-none">
        <div className="flex items-start justify-end">
          <span className="font-mono-label bg-[#111]/80 backdrop-blur-sm text-white px-3 py-1.5 flex items-center gap-1.5">
            <Images size={12} strokeWidth={1.5} />
            {project.images.length} {t("projects.photos")}
          </span>
        </div>
        <div className="bg-gradient-to-t from-[#111]/90 via-[#111]/40 to-transparent -m-6 md:-m-8 p-6 md:p-8 pt-16">
          <div className="font-mono-label text-white/70 mb-2">{meta.category} · {meta.location}</div>
          <div className="font-display text-2xl md:text-3xl mb-3">{meta.title}</div>
          <div className="inline-flex items-center gap-2 font-mono-label opacity-0 group-hover:opacity-100 transition-opacity">
            {t("projects.view_gallery")}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectModal = ({ project, meta, onClose, t }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  const goPrev = useCallback(
    () => setActiveIdx((i) => (i - 1 + project.images.length) % project.images.length),
    [project.images.length]
  );
  const goNext = useCallback(
    () => setActiveIdx((i) => (i + 1) % project.images.length),
    [project.images.length]
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose, goPrev, goNext]);

  return (
    <div
      data-testid="project-modal"
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full max-w-[1600px] mx-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="flex items-start justify-between px-6 md:px-12 pt-6 md:pt-10 text-white">
          <div className="max-w-2xl">
            <div className="font-mono-label text-white/60 mb-2">
              {meta.category} · {meta.location}
            </div>
            <h3 className="font-display text-2xl md:text-4xl">{meta.title}</h3>
          </div>
          <button
            data-testid="project-modal-close"
            onClick={onClose}
            aria-label={t("projects.close")}
            className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border border-white/30 text-white hover:bg-white hover:text-[#111] transition-colors"
          >
            <X size={22} strokeWidth={1.25} />
          </button>
        </div>

        {/* Image stage */}
        <div className="flex-1 flex items-center justify-center px-4 md:px-12 py-6 md:py-8 min-h-0">
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              key={project.images[activeIdx]}
              src={project.images[activeIdx]}
              alt={`${meta.title} ${activeIdx + 1}`}
              className="max-w-full max-h-full object-contain shadow-2xl animate-fadeIn"
            />

            <button
              data-testid="project-modal-prev"
              onClick={goPrev}
              aria-label={t("projects.prev")}
              className="absolute left-0 md:-left-2 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/10 hover:bg-white text-white hover:text-[#111] border border-white/30 backdrop-blur-sm transition-colors"
            >
              <ChevronLeft size={26} strokeWidth={1.25} />
            </button>
            <button
              data-testid="project-modal-next"
              onClick={goNext}
              aria-label={t("projects.next")}
              className="absolute right-0 md:-right-2 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/10 hover:bg-white text-white hover:text-[#111] border border-white/30 backdrop-blur-sm transition-colors"
            >
              <ChevronRight size={26} strokeWidth={1.25} />
            </button>
          </div>
        </div>

        {/* Footer / thumbs */}
        <div className="px-6 md:px-12 pb-6 md:pb-10">
          <div className="flex items-center justify-between mb-4 text-white">
            <div className="font-mono-label text-white/60">
              {String(activeIdx + 1).padStart(2, "0")} / {String(project.images.length).padStart(2, "0")}
            </div>
            <p className="hidden md:block max-w-md text-white/70 text-sm font-light">
              {meta.description}
            </p>
          </div>
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 thumb-scroll">
            {project.images.map((src, i) => (
              <button
                key={src}
                data-testid={`project-modal-thumb-${i}`}
                onClick={() => setActiveIdx(i)}
                className={`shrink-0 w-20 h-24 md:w-24 md:h-28 overflow-hidden border-2 transition-all ${
                  i === activeIdx ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const { t } = useLang();
  const items = t("projects.items");
  const [openId, setOpenId] = useState(null);

  if (!Array.isArray(items)) return null;

  // Match data with translations by id
  const merged = projectsData
    .map((p) => {
      const meta = items.find((it) => it.id === p.id);
      return meta ? { project: p, meta } : null;
    })
    .filter(Boolean);

  // Bento-style spans for 5 projects:
  //  [ Rescanya (tall hero) ][ Olivereta ]
  //  [        ...          ][ Nou Moles ]
  //  [ Aldaia ]            [ Castellón ]
  // Hero uses aspect on mobile + h-full on desktop to fill the 2-row span cleanly.
  const layouts = [
    { span: "lg:col-span-7 lg:row-span-2", media: "aspect-[4/5] lg:aspect-auto lg:h-full" },
    { span: "lg:col-span-5", media: "aspect-[4/5]" },
    { span: "lg:col-span-5", media: "aspect-[4/5]" },
    { span: "lg:col-span-6", media: "aspect-[4/5]" },
    { span: "lg:col-span-6", media: "aspect-[4/5]" },
  ];

  const openProject = merged.find(({ project }) => project.id === openId);

  return (
    <section id="proyectos" data-testid="projects-section" className="section-pad bg-[#FAFAFA]">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 lg:mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#666] mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#A89F91]" />
              {t("projects.kicker")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#111]">
              {t("projects.title")}
            </h2>
          </div>
          <div className="lg:col-span-3 lg:col-start-10">
            <button
              data-testid="projects-cta-btn"
              onClick={() =>
                document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-secondary"
            >
              {t("projects.all")}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6 auto-rows-auto">
          {merged.map(({ project, meta }, i) => (
            <ProjectCard
              key={project.id}
              project={project}
              meta={meta}
              index={i}
              span={layouts[i]?.span || "lg:col-span-6"}
              media={layouts[i]?.media || "aspect-[4/5]"}
              onOpen={setOpenId}
              t={t}
            />
          ))}
        </div>
      </div>

      {openProject && (
        <ProjectModal
          project={openProject.project}
          meta={openProject.meta}
          onClose={() => setOpenId(null)}
          t={t}
        />
      )}
    </section>
  );
};

export default Projects;
